import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import emailjs from "@emailjs/browser";
import Section from "@/theme/Section";
import Button from "@/theme/Button";
import { api, formatError } from "@/lib/api";
import { useContent } from "@/lib/content";

// EmailJS credentials — public key is intentionally client-side
// (EmailJS uses domain allowlist + rate limits for security, not key secrecy).
const EMAILJS_SERVICE_ID = "service_ze6llnq";
const EMAILJS_PUBLIC_KEY = "-9gFR4gGi2FiM3gDt";
const EMAILJS_TEMPLATE_ADMIN = "template_w15htrg";
const EMAILJS_TEMPLATE_CLIENT = "template_tl0b3q9";

const Contact = () => {
  const { siteContent } = useContent();
  const c = siteContent.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      // 1. Save to DB first — source of truth, shows up in admin inquiries
      await api.post("/contact", form);

      // 2. Fire email notifications in parallel — best-effort on top of DB save
      const emailParams = {
        name: form.name,
        email: form.email,
        from_name: form.name,
        from_email: form.email,
        title: form.message,
        message: form.message,
        phone: form.phone || "Not provided",
      };
      try {
        await Promise.all([
          emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, emailParams, {
            publicKey: EMAILJS_PUBLIC_KEY,
          }),
          emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_CLIENT, emailParams, {
            publicKey: EMAILJS_PUBLIC_KEY,
          }),
        ]);
      } catch (emailErr) {
        // Don't fail the whole submission if email delivery hiccups —
        // the inquiry is already in MongoDB and visible in the admin dashboard.
        console.error("EmailJS send failed:", emailErr);
      }

      toast.success("Signal received. We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, "Transmission failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      id="contact"
      testid="contact-section"
      eyebrow="Get in touch"
      eyebrowColor="#c8ff2a"
      title='Let&apos;s build something <span style="color:#c8ff2a">together</span>'
      subtitle="Have a game idea? Need a dev team? Drop us a message and we&apos;ll reply within 24 hours."
    >
      <Toaster
        theme="dark"
        position="top-right"
        style={{ zIndex: 10000 }}
        toastOptions={{
          style: {
            background: "#0d0d0d",
            border: "1px solid #c8ff2a",
            color: "#fff",
            borderRadius: 8,
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 12,
            letterSpacing: "0.04em",
          },
        }}
      />
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Channels */}
        <aside className="lg:col-span-4 space-y-5">
          {c.address && (
            <div className="flex items-start gap-4">
              <span
                className="w-11 h-11 flex items-center justify-center rounded-2xl shrink-0"
                style={{
                  background: "rgba(200,255,42,0.10)",
                  border: "1px solid #c8ff2a",
                }}
              >
                <MapPin size={16} className="text-[#c8ff2a]" />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#c8ff2a] font-bold">
                  Address
                </div>
                <div
                  className="text-sm mt-1 text-white/80 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: c.address }}
                />
              </div>
            </div>
          )}
          {c.phone && (
            <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="flex items-start gap-4 group">
              <span
                className="w-11 h-11 flex items-center justify-center rounded-2xl shrink-0"
                style={{
                  background: "rgba(255,42,143,0.10)",
                  border: "1px solid #ff2a8f",
                }}
              >
                <Phone size={16} className="text-[#ff2a8f]" />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#ff2a8f] font-bold">
                  Phone
                </div>
                <div className="text-base mt-1 text-white group-hover:text-[#ff2a8f] transition-colors">
                  {c.phone}
                </div>
              </div>
            </a>
          )}
          {c.email && (
            <a href={`mailto:${c.email}`} className="flex items-start gap-4 group">
              <span
                className="w-11 h-11 flex items-center justify-center rounded-2xl shrink-0"
                style={{
                  background: "rgba(42,214,255,0.10)",
                  border: "1px solid #2ad6ff",
                }}
              >
                <Mail size={16} className="text-[#2ad6ff]" />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#2ad6ff] font-bold">
                  Email
                </div>
                <div className="text-base mt-1 text-white group-hover:text-[#2ad6ff] transition-colors break-all">
                  {c.email}
                </div>
              </div>
            </a>
          )}
        </aside>

        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-8 p-6 md:p-9 space-y-4"
          style={{
            background: "rgba(13,13,13,0.75)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "24px",
            backdropFilter: "blur(14px)",
          }}
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Your name *" testid="contact-name" value={form.name} onChange={set("name")} required />
            <Field label="Email *" type="email" testid="contact-email" value={form.email} onChange={set("email")} required />
          </div>
          <Field label="Phone (optional)" testid="contact-phone" value={form.phone} onChange={set("phone")} />
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold block mb-2">
              Message *
            </label>
            <textarea
              data-testid="contact-message"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/15 text-white placeholder-white/30 focus:border-[#c8ff2a] focus:outline-none focus:ring-2 focus:ring-[#c8ff2a]/30 transition-colors resize-y"
              placeholder="Tell us what you're building, timeline, references, anything useful…"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            />
          </div>
          <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
            <span className="text-[10px] tracking-[0.28em] uppercase text-white/40">
              We typically reply within 24h.
            </span>
            <Button data-testid="contact-submit" type="submit" disabled={loading}>
              {loading ? (
                <><Loader2 size={14} className="animate-spin" /> Sending…</>
              ) : (
                <>Send message <Send size={14} strokeWidth={3} /></>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Section>
  );
};

const Field = ({ label, testid, type = "text", ...rest }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold block mb-2">
      {label}
    </label>
    <input
      data-testid={testid}
      type={type}
      className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/15 text-white placeholder-white/30 focus:border-[#c8ff2a] focus:outline-none focus:ring-2 focus:ring-[#c8ff2a]/30 transition-colors"
      style={{ fontFamily: "'Outfit', sans-serif" }}
      {...rest}
    />
  </div>
);

export default Contact;
